// src/components/assistant/AssistantShell.tsx
//
// Shell full-screen tipo ChatGPT (sin el navbar de módulos). Orquesta:
//  - sidebar (nuevo chat, secciones, historial, menú)
//  - panel principal: ChatPane (chat) o placeholders (Librería/Agentes) o
//    el panel de Personalización.
//  - carga el historial de conversaciones del usuario (GET /threads/me/list).
//
// Montado en la ruta /chat (ver App.tsx). chat.ucognet.pro aterriza aquí.
//
// Slice A: chat + historial funcionales. Librería / Agentes / Personalización
// son placeholders honestos (se construyen en las siguientes rebanadas: la
// Librería reusa apps/documents que YA existe; Personalización y Agentes
// requieren backend nuevo — ver auditoría).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { History, Menu, Sparkles, PanelLeftOpen, Minimize2 } from 'lucide-react';

import { getMyThreads, renameThread, threadDisplayTitle } from '@/lib/api';
import { useBranding } from '@/context/BrandingContext';
import { ChatPane } from './ChatPane';
import { LibraryView } from './LibraryView';
import { ThreadHeaderTitle } from './ThreadTitle';
import { requestDockOpen, type DockReturnState } from './activeThread';
import { PersonalizationPanel } from './PersonalizationPanel';
import { AgentsView } from './AgentsView';
import { AssistantSidebar, type AssistantView, type ThreadListItem } from './AssistantSidebar';
import { useModules } from '@/context/ModulesContext';
import { HistoryDrawer, ThreadHistoryPanel, useHistoryColumn } from './ThreadHistory';

interface AssistantShellProps {
  webChatHeaders?: Record<string, string>;
  onLogout: () => void;
}

export function AssistantShell({ webChatHeaders = {}, onLogout }: AssistantShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // «Ampliar» desde el chat flotante llega con ?hilo=<id>: abre ESA conversación.
  const [sp] = useSearchParams();
  const { orgName, orgLogo } = useBranding();
  const { availableModules, lastModuleKey, isAdmin } = useModules();
  // Agente ENFOCADO: para usuarios de portal (no-admin — p.ej. el asociado que
  // solo tiene su bandeja) el chat acota su catálogo de tools al módulo activo
  // (focus_tools → backend focus_module) → arranque veloz, sin las ~58 tools de
  // toda la org. Los admins conservan el catálogo completo (hacen tareas
  // cross-módulo). Fail-safe: si el módulo no mapea a tools, el backend cae al
  // catálogo normal, así que cero regresión para cualquier org.
  const _activeModuleKey =
    lastModuleKey && availableModules.some((m) => m.key === lastModuleKey)
      ? lastModuleKey
      : availableModules[0]?.key;
  const focusTools = !isAdmin && _activeModuleKey ? _activeModuleKey : undefined;
  const [view, setView] = useState<AssistantView>('chat');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(() => sp.get('hilo'));
  const [newChatNonce, setNewChatNonce] = useState(0);
  const [threads, setThreads] = useState<ThreadListItem[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [showPersonalization, setShowPersonalization] = useState(false);
  // Historial separado del menú: columna colapsable (escritorio) o cajón (móvil).
  const history = useHistoryColumn();
  // Sidebar: en desktop es columna (abierta por defecto); en móvil es un drawer
  // overlay (cerrado por defecto) para que NUNCA aplaste el chat.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window === 'undefined' || window.matchMedia('(min-width: 768px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => {
      setIsMobile(mq.matches);
      // Al pasar a desktop, abre; al pasar a móvil, cierra el overlay.
      setSidebarOpen(!mq.matches);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const refreshThreads = useCallback(async () => {
    try {
      const data = await getMyThreads();
      const list: ThreadListItem[] = (data?.threads || []).map((t: any) => ({
        id: t.id || t.thread_id || t.session_id,
        title: threadDisplayTitle(t).slice(0, 60),
        hasTitle: !!t.title,
        at: t.last_message_at || null,
      }));
      setThreads(list);
    } catch {
      setThreads([]);
    } finally {
      setLoadingThreads(false);
    }
  }, []);

  useEffect(() => {
    void refreshThreads();
  }, [refreshThreads]);

  // Hilo visible (el elegido o el que acaba de iniciar ChatPane) y su título.
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [titleOverride, setTitleOverride] = useState<Record<string, string | null>>({});
  const titleTimers = useRef<number[]>([]);
  useEffect(() => () => titleTimers.current.forEach((t) => window.clearTimeout(t)), []);

  // El backend nombra el hilo en segundo plano: refresca la lista un par de veces.
  const handleTitlePending = useCallback(() => {
    [2500, 7000].forEach((ms) => titleTimers.current.push(window.setTimeout(() => void refreshThreads(), ms)));
  }, [refreshThreads]);

  const handleThreadInfo = useCallback((id: string, info: { title: string | null }) => {
    setTitleOverride((m) => (m[id] === info.title ? m : { ...m, [id]: info.title }));
  }, []);

  const handleRename = useCallback(
    async (id: string, title: string) => {
      setThreads((list) => list.map((t) => (t.id === id ? { ...t, title, hasTitle: true } : t)));
      setTitleOverride((m) => ({ ...m, [id]: title }));
      try {
        await renameThread(id, title);
      } catch {
        void refreshThreads();
        setTitleOverride((m) => ({ ...m, [id]: null }));
      }
    },
    [refreshThreads],
  );

  const shownThreadId = selectedThreadId ?? currentThreadId;
  const listed = threads.find((t) => t.id === shownThreadId);
  const shownTitle =
    (shownThreadId && titleOverride[shownThreadId]) || (listed?.hasTitle ? listed.title : null) || null;

  // En móvil, cualquier acción de navegación cierra el drawer para revelar el chat.
  const closeOnMobile = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const handleNewChat = () => {
    setView('chat');
    setSelectedThreadId(null);
    setCurrentThreadId(null);
    setNewChatNonce((n) => n + 1);
    closeOnMobile();
    history.afterPick();
  };

  const handleSelectThread = (id: string) => {
    setView('chat');
    setSelectedThreadId(id);
    closeOnMobile();
    history.afterPick();
  };

  const historyPanel = (mobile: boolean) => (
    <ThreadHistoryPanel
      threads={threads}
      loading={loadingThreads}
      activeId={view === 'chat' ? shownThreadId : null}
      onNewChat={handleNewChat}
      onSelect={handleSelectThread}
      onRename={handleRename}
      onClose={mobile ? history.closeDrawer : history.collapseColumn}
      closeLabel={mobile ? 'Cerrar historial' : 'Ocultar historial'}
      closeIcon={mobile ? 'x' : 'collapse'}
    />
  );

  const handleSelectView = (v: AssistantView) => {
    setView(v);
    closeOnMobile();
  };

  const paneKey = selectedThreadId ?? `new-${newChatNonce}`;

  // «Minimizar»: vuelve al chat flotante con esta conversación (ver activeThread.ts).
  const handleMinimize = () => {
    requestDockOpen(shownThreadId);
    navigate((location.state as DockReturnState | null)?.fromDock || '/');
  };

  return (
    // h-[100dvh]: viewport dinámico → sin saltos por la barra de direcciones en iOS.
    // overscroll-none: mata el rebote/rubber-band que "movía toda la pantalla".
    <div className="flex h-[100dvh] w-full overflow-hidden overscroll-none bg-background">
      {/* Backdrop del drawer — solo móvil, cuando está abierto. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar: drawer overlay que se desliza en móvil; columna estática en desktop.
          En móvil NUNCA empuja el chat (era el "chat aplastado"). */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-[84%] max-w-[300px] transform transition-transform duration-300 ease-out md:static md:z-auto md:w-64 md:max-w-none md:translate-x-0 md:transition-none ${
          sidebarOpen ? 'translate-x-0 shadow-2xl md:shadow-none' : '-translate-x-full'
        } ${sidebarOpen ? 'md:block' : 'md:hidden'}`}
      >
        <AssistantSidebar
          orgName={orgName}
          orgLogo={orgLogo}
          view={view}
          activeThreadId={shownThreadId}
          threads={threads}
          loadingThreads={loadingThreads}
          onNewChat={handleNewChat}
          onSelectView={handleSelectView}
          onSelectThread={handleSelectThread}
          onRenameThread={handleRename}
          onOpenPersonalization={() => setShowPersonalization(true)}
          onBackToDashboard={() => navigate('/')}
          onLogout={onLogout}
          onCollapse={() => setSidebarOpen(false)}
          hideHistory
        />
      </div>

      {/* Historial (escritorio): columna colapsable junto al chat. */}
      {view === 'chat' && history.columnOpen && (
        <aside aria-label="Historial de conversaciones" className="hidden w-64 shrink-0 border-r border-border md:block">
          {historyPanel(false)}
        </aside>
      )}
      {/* Historial (móvil): cajón propio; el chat sigue montado detrás. */}
      <HistoryDrawer open={history.drawerOpen} onClose={history.closeDrawer}>
        {historyPanel(true)}
      </HistoryDrawer>

      <main className="relative flex min-w-0 flex-1 flex-col">
        {/* Barra superior — respeta el notch (safe-area top). */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] sm:px-4 sm:py-3">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menú de la app"
            title="Menú"
            className={`-ml-1 rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted active:bg-muted ${
              sidebarOpen ? 'md:hidden' : ''
            }`}
          >
            <Menu size={18} className="md:hidden" aria-hidden />
            <PanelLeftOpen size={18} className="hidden md:block" aria-hidden />
          </button>
          <Sparkles size={16} className="shrink-0 text-primary" />
          {view === 'chat' ? (
            <ThreadHeaderTitle
              title={shownTitle}
              fallback="Asistente"
              className="text-sm font-semibold"
              onRename={shownThreadId ? (t) => handleRename(shownThreadId, t) : undefined}
            />
          ) : (
            <span className="truncate text-sm font-semibold">{view === 'library' ? 'Librería' : 'Agentes'}</span>
          )}
          {view === 'chat' && (
            <button
              type="button"
              onClick={history.toggle}
              aria-label={history.isMobile ? 'Abrir historial de conversaciones' : history.columnOpen ? 'Ocultar historial' : 'Mostrar historial'}
              aria-expanded={history.isMobile ? history.drawerOpen : history.columnOpen}
              className="ml-auto flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted"
            >
              <History size={16} aria-hidden />
              <span className="hidden sm:inline">Historial</span>
            </button>
          )}
          {view === 'chat' && (
            <button
              type="button"
              onClick={handleMinimize}
              aria-label="Minimizar al chat flotante (la conversación sigue ahí)"
              title="Minimizar"
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted"
            >
              <Minimize2 size={16} aria-hidden />
              <span className="hidden sm:inline">Minimizar</span>
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1">
          {view === 'chat' && (
            <ChatPane
              key={paneKey}
              threadId={selectedThreadId}
              /* "Nuevo chat" (nonce>0 sin thread seleccionado) → fuerza thread nuevo.
                 Primera carga (nonce=0) → recupera el hilo del usuario (historial). */
              startFresh={!selectedThreadId && newChatNonce > 0}
              webChatHeaders={webChatHeaders}
              onThreadStarted={(id) => {
                setCurrentThreadId(id);
                void refreshThreads();
              }}
              focusTools={focusTools}
              modelPicker
              mentions
              shareThread
              onThreadInfo={handleThreadInfo}
              onTitlePending={handleTitlePending}
            />
          )}
          {view === 'library' && <LibraryView />}
          {view === 'agents' && <AgentsView />}
        </div>
      </main>

      {/* Panel de Personalización — formulario real (GET/PUT /me/preferences) */}
      {showPersonalization && (
        <PersonalizationPanel
          webChatHeaders={webChatHeaders}
          onClose={() => setShowPersonalization(false)}
        />
      )}
    </div>
  );
}

export default AssistantShell;
